import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import TrackArt from '../TrackArt';

const localArt = (p) => `http://localhost:3069/album-art?path=${encodeURIComponent(p)}`;
export const coverOf = (t) => t.cover || (t.path ? localArt(t.path) : null);
const MENU_WIDTH = 340;
const MENU_MAX_HEIGHT = 380;

const Check = () => (
    <svg className="amx-option-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
        <path d="M5 12.5l4.5 4.5L19 7.5" />
    </svg>
);

/**
 * Choose the record a set opens or closes with: a searchable track list in a
 * popover next to `anchorEl` (portalled to <body> — the rail scrolls and would
 * clip it). The first entry, "MixGoat picks", clears the pin.
 *
 * @param {HTMLElement} anchorEl
 * @param {object[]} options     the tracks in the set (rows from automixSlice)
 * @param {string|null} value    the pinned row key
 * @param {string|null} otherKey the row pinned at the other end (marked; picking it moves the pin)
 * @param {string} otherLabel    'Closer' | 'Opener'
 * @param {(key: string|null) => void} onChoose
 * @param {() => void} onClose
 */
const AutomixTrackMenu = ({ anchorEl, label, options, value, otherKey, otherLabel, onChoose, onClose }) => {
    const menuRef = useRef(null);
    const searchRef = useRef(null);
    const listRef = useRef(null);
    const [query, setQuery] = useState('');
    const [active, setActive] = useState(() => Math.max(0, options.findIndex((t) => t.key === value) + 1));
    const [pos, setPos] = useState(null);

    // entry 0 is always "MixGoat picks"
    const entries = useMemo(() => {
        const q = query.trim().toLowerCase();
        return [null, ...options.filter((t) => !q || `${t.title} ${t.artist}`.toLowerCase().includes(q))];
    }, [options, query]);

    useLayoutEffect(() => {
        const r = anchorEl?.getBoundingClientRect();
        if (!r) return;
        const width = Math.max(r.width, MENU_WIDTH);
        const left = Math.max(12, Math.min(r.right - width, window.innerWidth - width - 12));
        const below = window.innerHeight - r.bottom - 16;
        const above = r.top - 16;
        // open upward when the room below can't show a useful list
        if (below < 260 && above > below) setPos({ left, width, bottom: window.innerHeight - r.top + 6, maxHeight: Math.min(MENU_MAX_HEIGHT, above) });
        else setPos({ left, width, top: r.bottom + 6, maxHeight: Math.min(MENU_MAX_HEIGHT, below) });
    }, [anchorEl]);

    useEffect(() => {
        const focus = setTimeout(() => searchRef.current?.focus(), 0);
        const onDown = (e) => {
            if (menuRef.current?.contains(e.target) || anchorEl?.contains(e.target)) return;
            onClose();
        };
        // a scroll anywhere but inside the list would leave the menu floating
        const onScroll = (e) => { if (!menuRef.current?.contains(e.target)) onClose(); };
        document.addEventListener('mousedown', onDown);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onClose);
        return () => {
            clearTimeout(focus);
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onClose);
        };
    }, [anchorEl, onClose]);

    // keep the highlighted option in view while arrowing through the list
    useEffect(() => {
        listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
    }, [active]);

    const choose = (entry) => {
        onChoose(entry ? entry.key : null);
        onClose();
        anchorEl?.focus();
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') { e.preventDefault(); onClose(); anchorEl?.focus(); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(entries.length - 1, a + 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
        else if (e.key === 'Enter') { e.preventDefault(); if (entries[active] !== undefined) choose(entries[active]); }
    };

    if (!pos) return null;
    return createPortal(
        <div
            ref={menuRef}
            className="amx-track-menu"
            style={{ left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, maxHeight: pos.maxHeight }}
            onKeyDown={onKeyDown}
        >
            <div className="amx-track-menu-search">
                <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    placeholder="Find a track"
                    aria-label={`${label}: find a track`}
                    spellCheck={false}
                    onChange={(e) => { setQuery(e.target.value); setActive(0); }}
                />
            </div>
            <div className="amx-track-menu-list" ref={listRef} role="listbox" aria-label={label}>
                {entries.map((t, i) => {
                    const isSel = t ? t.key === value : value == null;
                    return (
                        <button
                            type="button"
                            key={t ? t.key : '__auto'}
                            data-index={i}
                            role="option"
                            aria-selected={isSel}
                            className={`amx-option${i === active ? ' is-active' : ''}${isSel ? ' is-selected' : ''}${t ? '' : ' is-auto'}`}
                            onMouseEnter={() => setActive(i)}
                            onClick={() => choose(t)}
                        >
                            {t ? (
                                <>
                                    <TrackArt className="amx-option-cover" src={coverOf(t)} size={32} />
                                    <span className="amx-option-text">
                                        <span className="amx-option-title">{t.title}</span>
                                        <span className="amx-option-sub">
                                            {[t.artist, t.bpm ? `${Math.round(t.bpm)} BPM` : '', t.musicalKey].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                    {t.key === otherKey && <span className="amx-option-tag">{otherLabel}</span>}
                                </>
                            ) : (
                                <span className="amx-option-text">
                                    <span className="amx-option-title">MixGoat picks</span>
                                    <span className="amx-option-sub">Whatever fits the flow best</span>
                                </span>
                            )}
                            {isSel && <Check />}
                        </button>
                    );
                })}
                {entries.length === 1 && query.trim() && (
                    <div className="amx-option-empty">No track matches “{query.trim()}”</div>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default AutomixTrackMenu;
